import { useState, useCallback, useRef } from 'react'
import apiService from '../../../services/ApiService'
import { NEWS_REVIEW_STATUS, ALL_REVIEW_STATUSES, CONFIDENCE_THRESHOLDS } from '../constants/newsReviewStatus'

const PAGE_SIZE = 12

const DEFAULT_FILTERS = {
  status: NEWS_REVIEW_STATUS.PENDING_REVIEW,
  // Standardised on `countries` (plural) to match the documents tab and the
  // backend's canonical query-param name. Backend still accepts `country`
  // as a deprecated alias during the transition window.
  countries: [],
  source: [],
  search: '',
  sortBy: 'newsConfidence',
  sortDir: 'asc',
  confidenceMin: 0,
  confidenceMax: CONFIDENCE_THRESHOLDS.LOW_MAX,
  dateFrom: '',
  dateTo: '',
}

export const useNewsManagement = (initialFilters = {}, initialPageSize = PAGE_SIZE) => {
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...initialFilters })
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [pageSize, setPageSize] = useState(initialPageSize)

  // Track optimistic undo: articleId - previous reviewStatus
  const undoHistoryRef = useRef({})

  const normalizePageResponse = (response) => {
    if (response?.content) {
      return {
        items: response.content,
        totalPages: response.totalPages ?? 1,
        totalItems: response.totalElements ?? 0,
      }
    }
    if (Array.isArray(response)) {
      return {
        items: response,
        totalPages: Math.max(1, Math.ceil(response.length / PAGE_SIZE)),
        totalItems: response.length,
      }
    }
    return { items: [], totalPages: 1, totalItems: 0 }
  }

  const buildParams = useCallback(
    (page, overrideFilters, overridePageSize) => {
      const f = overrideFilters ?? filters
      const params = {
        page: page - 1, // 0-based
        size: overridePageSize ?? pageSize,
      }

      params.status = f.status
        ? f.status
        : ALL_REVIEW_STATUSES
      if (f.countries?.length) params.countries = f.countries
      if (f.source?.length) params.source = f.source
      if (f.search?.trim()) params.search = f.search.trim()
      if (f.sortBy) params.sortBy = f.sortBy
      if (f.sortDir) params.sortDir = f.sortDir
      if (f.confidenceMin !== undefined && f.confidenceMin !== '')
        params.confidenceMin = f.confidenceMin
      if (f.confidenceMax !== undefined && f.confidenceMax !== '')
        params.confidenceMax = f.confidenceMax
      if (f.dateFrom) params.dateFrom = f.dateFrom
      if (f.dateTo) params.dateTo = f.dateTo
      return params
    },
    [filters, pageSize],
  )

  const loadArticles = useCallback(
    async (page = currentPage, overrideFilters, overridePageSize) => {
      try {
        setLoading(true)
        const params = buildParams(page, overrideFilters, overridePageSize)
        const response = await apiService.getAdminNewsForReview(params)
        const { items, totalPages: tp, totalItems: ti } = normalizePageResponse(response)
        setArticles(items)
        setTotalPages(tp)
        setTotalItems(ti)
      } catch (error) {
        console.error('Failed to load news for review:', error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [currentPage, buildParams],
  )

  // Single Article Actions

  /**
   * Optimistically remove a single article from local state.
   * Stores the full article object for potential rollback.
   */
  const removeArticleLocally = useCallback((id) => {
    setArticles((prev) => {
      const article = prev.find((a) => a.id === id)
      if (article) undoHistoryRef.current[id] = article
      return prev.filter((a) => a.id !== id)
    })
    setTotalItems((prev) => Math.max(0, prev - 1))
  }, [])

  const approveArticle = useCallback(
    async (id) => {
      removeArticleLocally(id)
      try {
        await apiService.updateNewsReviewStatus(id, { reviewStatus: NEWS_REVIEW_STATUS.APPROVED })
      } catch (error) {
        // Roll back on failure — add article back
        const saved = undoHistoryRef.current[id]
        if (saved) {
          setArticles((prev) => [saved, ...prev])
          setTotalItems((prev) => prev + 1)
          delete undoHistoryRef.current[id]
        }
        throw error
      }
    },
    [removeArticleLocally],
  )

  const rejectArticle = useCallback(
    async (id) => {
      removeArticleLocally(id)
      try {
        await apiService.updateNewsReviewStatus(id, { reviewStatus: NEWS_REVIEW_STATUS.REJECTED })
      } catch (error) {
        const saved = undoHistoryRef.current[id]
        if (saved) {
          setArticles((prev) => [saved, ...prev])
          setTotalItems((prev) => prev + 1)
          delete undoHistoryRef.current[id]
        }
        throw error
      }
    },
    [removeArticleLocally],
  )

  const undoAction = useCallback(
    async (id) => {
      // Capture the article for rollback before removing it.
      // Articles may be loaded fresh from server (not in undoHistoryRef), save them in a temp ref slot inside the setState updater which runs synchronously.
      const tempKey = `__undo_${id}`
      setArticles((prev) => {
        const found = prev.find((a) => a.id === id)
        if (found) undoHistoryRef.current[tempKey] = found
        return prev.filter((a) => a.id !== id)
      })
      setTotalItems((prev) => Math.max(0, prev - 1))
      try {
        await apiService.updateNewsReviewStatus(id, { reviewStatus: NEWS_REVIEW_STATUS.PENDING_REVIEW })
        delete undoHistoryRef.current[id]
        delete undoHistoryRef.current[tempKey]
      } catch (error) {
        // Roll back: restore article to list
        const saved = undoHistoryRef.current[tempKey] || undoHistoryRef.current[id]
        if (saved) {
          setArticles((prev) => [saved, ...prev])
          setTotalItems((prev) => prev + 1)
        }
        delete undoHistoryRef.current[tempKey]
        console.error('Undo failed:', error)
        throw error
      }
    },
    [],
  )

  // Country Update
  const updateArticleCountries = useCallback(async (id, countries) => {
    // Optimistic update
    setArticles((prev) =>
      prev.map((a) => (a.id === id ? { ...a, countries } : a)),
    )
    try {
      await apiService.updateNewsCountry(id, countries)
    } catch (error) {
      console.error('Failed to update countries:', error)
      throw error
    }
  }, [])

  // Bulk Actions
  const bulkApprove = useCallback(
    async (ids) => {
      const idArray = [...ids]
      setArticles((prev) => prev.filter((a) => !idArray.includes(a.id)))
      setTotalItems((prev) => Math.max(0, prev - idArray.length))
      try {
        await apiService.bulkUpdateNewsReviewStatus(idArray, {
          reviewStatus: NEWS_REVIEW_STATUS.APPROVED,
        })
        setSelectedIds(new Set())
      } catch (error) {
        // Reload from server on failure
        loadArticles()
        throw error
      }
    },
    [loadArticles],
  )

  const bulkReject = useCallback(
    async (ids) => {
      const idArray = [...ids]
      setArticles((prev) => prev.filter((a) => !idArray.includes(a.id)))
      setTotalItems((prev) => Math.max(0, prev - idArray.length))
      try {
        await apiService.bulkUpdateNewsReviewStatus(idArray, {
          reviewStatus: NEWS_REVIEW_STATUS.REJECTED,
        })
        setSelectedIds(new Set())
      } catch (error) {
        loadArticles()
        throw error
      }
    },
    [loadArticles],
  )

  /**
   * Bulk assign country: appends countryCode to each selected article's countries[].
   * Sends full updated countries[] array in the PATCH call.
   */
  const bulkAssignCountry = useCallback(async (ids, countryCodes) => {
    const codes = Array.isArray(countryCodes) ? countryCodes : [countryCodes]
    const idArray = [...ids]
    const updatedMap = {}
    setArticles((prev) =>
      prev.map((a) => {
        if (!idArray.includes(a.id)) return a
        const existing = Array.isArray(a.countries) ? a.countries : []
        const updated = [...new Set([...existing, ...codes])]
        updatedMap[a.id] = updated
        return { ...a, countries: updated }
      }),
    )
    try {
      await Promise.all(
        idArray.map((id) =>
          apiService.updateNewsCountry(id, updatedMap[id] ?? codes),
        ),
      )
      setSelectedIds(new Set())
    } catch (error) {
      console.error('Bulk assign country failed:', error)
      throw error
    }
  }, [])

  // Selection
  const toggleSelection = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(articles.map((a) => a.id)))
  }, [articles])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  // Filters & Sorting
  const handleFilterChange = useCallback(
    (key, value) => {
      const newFilters = { ...filters, [key]: value }
      setFilters(newFilters)
      setCurrentPage(1)
      loadArticles(1, newFilters)
    },
    [filters, loadArticles],
  )

  const handleFiltersChange = useCallback(
    (partialFilters) => {
      const newFilters = { ...filters, ...partialFilters }
      setFilters(newFilters)
      setCurrentPage(1)
      loadArticles(1, newFilters)
    },
    [filters, loadArticles],
  )

  const handleSearch = useCallback(
    (search) => {
      handleFilterChange('search', search)
    },
    [handleFilterChange],
  )

  const handleSortChange = useCallback(
    (sortBy, sortDir) => {
      const newFilters = { ...filters, sortBy, sortDir }
      setFilters(newFilters)
      setCurrentPage(1)
      loadArticles(1, newFilters)
    },
    [filters, loadArticles],
  )

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page)
      loadArticles(page)
    },
    [loadArticles],
  )

  const handlePageSizeChange = useCallback(
    (newSize) => {
      setPageSize(newSize)
      setCurrentPage(1)
      loadArticles(1, undefined, newSize)
    },
    [loadArticles],
  )

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
    setCurrentPage(1)
    loadArticles(1, DEFAULT_FILTERS)
  }, [loadArticles])

  return {
    articles,
    loading,
    currentPage,
    totalPages,
    totalItems,
    filters,
    selectedIds,
    pageSize,
    // Data loading
    loadArticles,
    // Single actions
    approveArticle,
    rejectArticle,
    undoAction,
    updateArticleCountries,
    // Bulk actions
    bulkApprove,
    bulkReject,
    bulkAssignCountry,
    // Selection
    toggleSelection,
    selectAll,
    clearSelection,
    // Filters / sort / page
    handleFilterChange,
    handleFiltersChange,
    handleSearch,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
    resetFilters,
  }
}
